<template>
    <UiModalMobile :isOpen="show" @close="$emit('close')" title="">
        <template #header>
            <transition name="fade" mode="out-in">
                <div
                    v-if="!showDeleteConfirmation"
                    key="header-content"
                    class="flex items-center justify-between w-full"
                >
                    <div
                        data-connected-bank="false"
                        data-dropdown="false"
                        data-hidden="true"
                        data-last-4-digits="true"
                        class="self-stretch inline-flex flex-col justify-start items-center"
                    >
                        <div
                            class="self-stretch inline-flex justify-between items-start"
                        >
                            <div class="flex justify-start items-center">
                                <button
                                    type="button"
                                    class="flex p-2 items-start gap-2 rounded-md hover:bg-slate-100 dark:bg-slate-700"
                                    :class="{
                                        'opacity-50 cursor-not-allowed':
                                            isFirstCard,
                                    }"
                                    aria-label="Previous card"
                                    @click="navigateToPreviousCard"
                                    :disabled="isFirstCard"
                                >
                                    <Icon
                                        icon="chevron-up"
                                        :width="20"
                                        :height="20"
                                    />
                                </button>
                                <button
                                    type="button"
                                    class="flex p-2 items-start gap-2 rounded-md hover:bg-slate-100 dark:bg-slate-700 ml-2"
                                    :class="{
                                        'opacity-50 cursor-not-allowed':
                                            isLastCard,
                                    }"
                                    aria-label="Next card"
                                    @click="navigateToNextCard"
                                    :disabled="isLastCard"
                                >
                                    <Icon
                                        icon="chevron-down"
                                        :width="20"
                                        :height="20"
                                    />
                                </button>
                                <div
                                    class="flex pl-2 gap-2 justify-start items-start text-slate-400 text-sm font-normal leading-5"
                                >
                                    {{ currentCardIndex }} of
                                    {{ totalCards }} cards
                                </div>
                            </div>
                        </div>
                    </div>
                    <button
                        @click="$emit('close')"
                        class="btn-base btn-icon !m-0"
                    >
                        <Icon icon="x" class="text-slate-500 dark:text-slate-400" />
                    </button>
                </div>
            </transition>
        </template>
        <template #content>
            <TransitionGroup name="fade">
                <!-- Delete Confirmation -->
                <div
                    v-if="showDeleteConfirmation"
                    key="delete-confirmation"
                    class="space-y-6"
                >
                    <div class="flex items-center gap-3 self-stretch">
                        <div
                            class="flex items-center justify-center w-12 h-12 p-3 rounded-full bg-red-100"
                        >
                            <Icon icon="trash" :pathClass="'fill-red-500'" />
                        </div>
                        <div class="flex-1">
                            <h3
                                class="text-slate-900 dark:text-slate-100 text-xl font-medium leading-7 mb-2"
                            >
                                Delete {{ selectedCard?.card?.title
                                }}{{
                                    !selectedCard?.card?.title
                                        ?.toLowerCase()
                                        .includes('card')
                                        ? ' Card'
                                        : ''
                                }}?
                            </h3>
                            <p class="text-slate-600 dark:text-slate-300 text-sm leading-5">
                                Are you sure you want to delete this card? This
                                also deletes its benefit usage history,
                                including credits logged on it, and its benefit
                                settings. This action cannot be undone.
                            </p>
                        </div>
                    </div>

                    <!-- Action Buttons -->
                    <div class="flex gap-3 justify-end">
                        <button
                            @click="cancelDelete"
                            class="px-4 py-2 text-sm font-medium text-slate-700 dark:text-slate-200 bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded-md hover:bg-slate-50 dark:hover:bg-slate-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-slate-500 transition-colors duration-150"
                        >
                            Cancel
                        </button>
                        <button
                            @click="confirmDelete"
                            class="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-colors duration-150"
                        >
                            Delete
                        </button>
                    </div>
                </div>
                <div key="card-details" v-else>
                    <div class="flex justify-center mb-4 mt-4">
                        <span class="block text-center w-full relative">
                            <div>
                                <ImageLoader v-if="selectedCard?.card">
                                    <template #image>
                                        <div>
                                            <img
                                                :src="`${cdnUrl}/${selectedCard.card.card_image?.path}/_small/${selectedCard.card.card_image?.file_name}`"
                                                class="mx-auto w-full h-auto max-w-[90vw] lg:w-[352px] lg:h-[216px] object-contain"
                                            />
                                        </div>
                                    </template>
                                    <template #error>
                                        <div
                                            class="flex flex-col items-center justify-center mx-auto w-[352px] h-[216px] bg-gradient-to-r from-slate-100 to-slate-200 border border-slate-300 rounded-lg shadow-sm overflow-hidden relative"
                                        >
                                            <div
                                                class="absolute top-0 left-0 w-16 h-10 bg-slate-300 m-4 rounded-md"
                                            ></div>
                                            <div
                                                class="absolute bottom-6 left-0 w-12 h-8 bg-slate-300 mx-4 rounded-md"
                                            ></div>
                                            <div
                                                class="absolute bottom-6 right-0 w-12 h-8 bg-slate-300 mx-4 rounded-md"
                                            ></div>

                                            <div
                                                class="flex flex-col items-center justify-center p-4 text-center w-full"
                                            >
                                                <div
                                                    class="text-slate-700 font-medium text-xl mb-2 w-full max-w-[90%] truncate"
                                                >
                                                    {{
                                                        selectedCard.unmatched
                                                            ? 'Unmatched Card'
                                                            : selectedCard.card
                                                                  ?.title ||
                                                              'Card'
                                                    }}
                                                </div>
                                                <div
                                                    class="text-slate-500 dark:text-slate-400 text-sm"
                                                >
                                                    {{
                                                        selectedCard.institution
                                                            ?.name ||
                                                        'Bank Name'
                                                    }}
                                                </div>
                                            </div>

                                            <div
                                                class="absolute bottom-14 left-4 right-4"
                                            >
                                                <div
                                                    class="flex justify-between items-center"
                                                >
                                                    <div
                                                        class="text-slate-600 dark:text-slate-300 text-xs"
                                                    >
                                                        {{
                                                            selectedCard.last_four
                                                                ? '••••  ••••  ••••  ' +
                                                                  selectedCard.last_four
                                                                : '••••  ••••  ••••  ••••'
                                                        }}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </template>
                                    <template #preloader>
                                        <div
                                            class="w-full lg:w-[352px] lg:h-[216px] rounded-lg overflow-hidden shrink-0 shimmer"
                                        ></div>
                                    </template>
                                </ImageLoader>
                                <div
                                    v-if="selectedCard && !selectedCard.card_id"
                                    class="flex flex-col items-center justify-center mx-auto w-[352px] h-[216px] bg-gradient-to-r from-slate-100 to-slate-200 border border-slate-300 rounded-lg shadow-sm overflow-hidden relative"
                                >
                                    <div
                                        class="absolute top-0 left-0 w-16 h-10 bg-slate-300 m-4 rounded-md"
                                    ></div>
                                    <div
                                        class="absolute bottom-6 left-0 w-12 h-8 bg-slate-300 mx-4 rounded-md"
                                    ></div>
                                    <div
                                        class="absolute bottom-6 right-0 w-12 h-8 bg-slate-300 mx-4 rounded-md"
                                    ></div>

                                    <div
                                        class="flex flex-col items-center justify-center p-4 text-center w-full"
                                    >
                                        <div
                                            class="text-slate-700 font-medium text-xl mb-2 w-full max-w-[90%] truncate"
                                        >
                                            {{
                                                selectedCard.unmatched
                                                    ? 'Unmatched Card'
                                                    : selectedCard.card
                                                          ?.title || 'Card'
                                            }}
                                        </div>
                                        <div class="text-slate-500 dark:text-slate-400 text-sm">
                                            {{
                                                selectedCard.institution
                                                    ?.name || 'Bank Name'
                                            }}
                                        </div>
                                    </div>

                                    <div
                                        class="absolute bottom-14 left-4 right-4"
                                    >
                                        <div
                                            class="flex justify-between items-center"
                                        >
                                            <div class="text-slate-600 dark:text-slate-300 text-xs">
                                                {{
                                                    selectedCard.last_four
                                                        ? '••••  ••••  ••••  ' +
                                                          selectedCard.last_four
                                                        : '••••  ••••  ••••  ••••'
                                                }}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <!-- Hidden badge overlay -->
                                <Transition name="fade">
                                    <div
                                        v-if="isHiddenCard"
                                        class="absolute bottom-2 right-4 inline-flex items-center gap-2 px-3 py-1.5 rounded-full"
                                        :class="hiddenBadgeClasses"
                                    >
                                        <Icon icon="eye-off" class="w-3 h-3" />
                                        <span
                                            class="text-xs font-medium leading-4"
                                        >
                                            Hidden in wallet
                                        </span>
                                    </div>
                                </Transition>
                            </div>
                        </span>
                    </div>
                    <div
                        class="flex flex-col items-center justify-center w-full"
                    >
                        <div class="text-center">
                            <div
                                class="inline-block text-center w-full max-w-[90%]"
                            >
                                <span
                                    v-if="selectedCard?.card"
                                    class="text-xl font-medium text-slate-900 dark:text-slate-100"
                                >
                                    {{
                                        selectedCard.unmatched
                                            ? 'Unmatched Card'
                                            : selectedCard.card.title
                                    }}
                                </span>
                                <span
                                    v-if="selectedCard?.last_four"
                                    class="text-xl font-normal text-slate-400 dark:text-slate-500 font-sans ml-1"
                                >
                                    ({{ selectedCard.last_four }})
                                </span>
                            </div>
                        </div>
                        <div
                            v-if="
                                selectedCard?.plaid_account_id ||
                                selectedCard?.connection_source ||
                                selectedCard?.is_connected ||
                                selectedCard?.source === 'connect_to_bank'
                            "
                            class="pt-2 flex items-center gap-2 rounded-full"
                        >
                            <span>
                                <Icon
                                    icon="bank-financial"
                                    :width="17"
                                    :height="16"
                                />
                            </span>
                            <span
                                class="text-sm font-normal leading-5"
                                :class="{ 'text-slate-400': !connectionProblem, 'text-red-500': connectionProblem }"
                            >
                                Connected via bank{{ connectionProblem ? ': Error' : '' }}
                            </span>
                        </div>
                        <div
                            v-if="selectedCard?.card"
                            class="flex justify-center items-center gap-2 pt-4 w-[368px]"
                        >
                            <button
                                class="flex items-center gap-2 px-4 py-2 rounded-[4px] border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 shadow-sm hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors duration-150"
                                type="button"
                                @click.prevent="openEditCardModal"
                                id="hs-wallet-edit-card"
                            >
                                <span>
                                    <Icon
                                        icon="pencil"
                                        :width="20"
                                        :height="20"
                                        class="text-slate-800 dark:text-slate-100 scale-125"
                                    />
                                </span>
                                <span
                                    class="text-slate-900 dark:text-slate-100 text-sm font-medium leading-5"
                                >
                                    Edit
                                </span>
                            </button>
                            <div class="relative" ref="moreMenuContainer">
                                <button
                                    class="flex items-center justify-center w-9 h-9 p-2 rounded-[4px] border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 shadow-sm hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors duration-150"
                                    type="button"
                                    id="hs-wallet-extra-action"
                                    @click="toggleMoreMenu"
                                >
                                    <Icon
                                        icon="dots-vertical"
                                        :width="20"
                                        :height="20"
                                    />
                                </button>
                                <!-- Popover Menu -->
                                <div
                                    v-if="showMoreMenu"
                                    class="absolute right-0 mt-2 flex p-2 flex-col items-start border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 shadow-sm z-50 min-w-[180px]"
                                    @click.stop
                                >
                                    <!-- Copy card details -->
                                    <button
                                        @click="copyCardDetails"
                                        class="flex w-full px-2 py-1.5 items-center gap-2 rounded hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors duration-150"
                                    >
                                        <Icon
                                            icon="copy"
                                            :width="20"
                                            :height="20"
                                        />
                                        <span
                                            class="text-slate-900 dark:text-slate-100 text-sm font-normal leading-5"
                                        >
                                            Copy card details
                                        </span>
                                    </button>

                                    <!-- Toggle visibility -->
                                    <button
                                        @click="toggleCardVisibility"
                                        class="flex w-full px-2 py-1.5 items-center gap-2 rounded hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors duration-150"
                                    >
                                        <Icon
                                            :icon="
                                                isHiddenCard
                                                    ? 'eye-on'
                                                    : 'eye-off'
                                            "
                                            :width="20"
                                            :height="20"
                                        />
                                        <span
                                            class="text-slate-900 dark:text-slate-100 text-sm font-normal leading-5"
                                        >
                                            {{
                                                isHiddenCard
                                                    ? 'Show in wallet'
                                                    : 'Hide in wallet'
                                            }}
                                        </span>
                                    </button>

                                    <!-- Delete card -->
                                    <button
                                        v-if="
                                            !(
                                                selectedCard?.plaid_account_id ||
                                                selectedCard?.connection_source ||
                                                selectedCard?.is_connected ||
                                                selectedCard?.source ===
                                                    'connect_to_bank'
                                            )
                                        "
                                        @click="showDeleteConfirmation = true"
                                        class="flex w-full px-2 py-1.5 items-center gap-2 rounded hover:bg-slate-50 dark:hover:bg-slate-700 text-red-500 transition-colors duration-150"
                                    >
                                        <Icon
                                            icon="delete"
                                            :width="20"
                                            :height="20"
                                        />
                                        <span
                                            class="text-sm font-normal leading-5"
                                        >
                                            Delete
                                        </span>
                                    </button>
                                </div>
                            </div>
                        </div>
                        <div class="flex items-center gap-2 relative" v-else>
                            <button
                                class="btn-base btn-white"
                                @click.stop="handleMatchCard()"
                            >
                                Match
                            </button>
                            <div
                                v-if="
                                    selectedCard &&
                                    selectedCard.card_match_request &&
                                    selectedCard.card_match_request.rejected
                                "
                                class="absolute -bottom-1.5 -right-1 inline-flex items-center justify-center w-5 h-5 rounded-full border-2 border-white bg-red-500 text-white shadow-lg"
                            >
                                <Icon icon="alert" class="w-2.5 h-2.5" />
                            </div>
                        </div>
                    </div>
                    <CardDetails
                        :user="selectedUser"
                        @add-product-change="handleAddProductChange"
                        @edit-product-change="handleEditProductChange"
                        @delete-product-change="handleDeleteProductChange"
                    />
                </div>
            </TransitionGroup>
        </template>
    </UiModalMobile>
</template>

<script setup>
import { ref, computed } from 'vue';
import { storeToRefs } from 'pinia';
import { useWalletStore } from '@/store/wallet';
import UiModalMobile from '@/components/ui/modal/ui-modal-mobile.vue';
import Icon from '@/components/ui/icon/icon.vue';
import ImageLoader from '@/components/ui/image-loader/image-loader.vue';
import AnnualFee from '@/components/wallet/annual-fee.vue';
import CardDetails from '@/components/wallet/partials/card-details.vue';
import { notify } from 'notiwind';

const props = defineProps({
    show: {
        type: Boolean,
        default: false,
    },
});

const emit = defineEmits(['close', 'open-edit-card', 'card-updated']);

const walletStore = useWalletStore();
const {
    selectedCard,
    visibleCards,
    groupedCards,
    groupBy,
    subgroupBy,
    groupSameCards,
    walletCards,
} = storeToRefs(walletStore);

const {
    setSelectedCard,
    setShowEditCardModal,
    updateCardVisibility,
    selectedUser,
    openCardMatchModal,
    getConnectionProblemForCard,
} = walletStore;

// Change history toggle state
const showChangeHistory = ref(false);

// More menu state
const showMoreMenu = ref(false);
const moreMenuContainer = ref(null);

// Delete confirmation state
const showDeleteConfirmation = ref(false);

const toggleChangeHistory = () => {
    showChangeHistory.value = !showChangeHistory.value;
};

// Helper functions
const formatNumber = (value) => {
    if (value === null || value === undefined) return '0';
    return new Intl.NumberFormat('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    }).format(value);
};

const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
        });
    } catch {
        return dateString;
    }
};

const cdnUrl = computed(() => {
    return window.cdnUrl;
});

const isHiddenCard = computed(() => {
    return selectedCard.value?.ignore;
});

const connectionProblem = computed(() => {
    return getConnectionProblemForCard(selectedCard.value);
});

const hiddenBadgeClasses = computed(() => {
    const isLightBackground =
        selectedCard.value?.card?.is_light_background || false;
    return getBadgeClasses(isLightBackground);
});

// Card navigation computed properties
const flattenedCards = computed(() => {
    // standardizes the cards for navigation that matches the order in the wallet table
    return recursivelyFlattenCards(visibleCards.value, groupedCards.value);
});

const currentCardIndex = computed(() => {
    if (!flattenedCards.value || !selectedCard.value) return 0;
    const idx = flattenedCards.value.findIndex(
        (card) => card.id === selectedCard.value.id
    );
    return idx >= 0 ? idx + 1 : 0;
});

const totalCards = computed(() => {
    if (!flattenedCards.value) return 0;
    return flattenedCards.value.length;
});

const isFirstCard = computed(() => {
    return currentCardIndex.value <= 1;
});

const isLastCard = computed(() => {
    return currentCardIndex.value >= totalCards.value;
});

// Card navigation methods
const navigateToPreviousCard = () => {
    if (isFirstCard.value) return;

    if (currentCardIndex.value > 0) {
        walletStore.setSelectedCard(
            flattenedCards.value[currentCardIndex.value - 2]
        );
    }
};

const handleMatchCard = () => {
    openCardMatchModal(selectedCard.value);
    emit('close');
};

const navigateToNextCard = () => {
    if (isLastCard.value) return;
    if (currentCardIndex.value < flattenedCards.value.length) {
        walletStore.setSelectedCard(
            flattenedCards.value[currentCardIndex.value]
        );
    }
};

const getBadgeClasses = (isLightBackground) => {
    return isLightBackground
        ? 'bg-slate-800 text-white'
        : 'bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100';
};

// Helper function to extract cards from items (handles groupSameCards logic)
const extractCardsFromItems = (items) => {
    if (!Array.isArray(items)) return [];

    const result = [];

    items.forEach((item) => {
        if (
            groupSameCards.value &&
            item &&
            item.cards &&
            Array.isArray(item.cards)
        ) {
            result.push(...item.cards);
        } else if (item) {
            result.push(item);
        }
    });

    return result;
};

// Helper function to recursively flatten cards (matches account-sidebar.vue logic)
const recursivelyFlattenCards = (visibleCards, groupedCards) => {
    const result = [];

    // Handle case when no grouping is applied
    if (!groupBy.value && visibleCards) {
        return extractCardsFromItems(visibleCards);
    }

    // Handle grouped data
    if (groupedCards && typeof groupedCards === 'object') {
        // Check if this is grouped + subgrouped (object of objects)
        if (subgroupBy.value) {
            // groupedCards = { primaryGroup: { subgroup: [items] } }
            Object.values(groupedCards).forEach((subgroupsObj) => {
                if (subgroupsObj && typeof subgroupsObj === 'object') {
                    Object.values(subgroupsObj).forEach((subgroupCards) => {
                        if (Array.isArray(subgroupCards)) {
                            result.push(
                                ...extractCardsFromItems(subgroupCards)
                            );
                        }
                    });
                }
            });
        } else {
            // grouped, not subgrouped (object of arrays)
            // groupedCards = { group: [items] }
            Object.values(groupedCards).forEach((groupCards) => {
                if (Array.isArray(groupCards)) {
                    result.push(...extractCardsFromItems(groupCards));
                }
            });
        }
    }

    return result;
};

// Button action methods
const openEditCardModal = () => {
    setSelectedCard(selectedCard.value);
    setShowEditCardModal(true);
    emit('close');
};

const toggleMoreMenu = () => {
    showMoreMenu.value = !showMoreMenu.value;
};

const copyCardDetails = () => {
    walletStore.setShowCopyCardModal(true);
    emit('close');
};

const toggleCardVisibility = async () => {
    if (!selectedCard.value) return;

    const hidden = !selectedCard.value.ignore;

    try {
        const response = await walletStore.updateCardVisibility(
            selectedCard.value.id,
            hidden
        );

        if (response.data.success) {
            walletStore.setSelectedCard({
                ...selectedCard.value,
                ignore: response.data.ignore,
            });
        }
    } catch (error) {
        console.error('Failed to update card visibility:', error);

        // Show error notification
        notify({
            group: 'error',
            title: 'Error',
            text: 'Failed to update card visibility. Please try again.',
        });
    } finally {
        showMoreMenu.value = false;
    }
};

const handleDelete = () => {
    showDeleteConfirmation.value = true;
    showMoreMenu.value = false;
};

const cancelDelete = () => {
    showDeleteConfirmation.value = false;
};

const confirmDelete = async () => {
    if (!selectedCard.value) return;

    try {
        // Call the delete API
        await walletStore.deleteCard(selectedCard.value);

        // Show success notification
        notify({
            group: 'success',
            title: 'Success',
            text: 'Card deleted successfully!',
        });

        // Close the modal
        emit('close');
    } catch (error) {
        console.error('Failed to delete card:', error);

        // Show error notification
        notify({
            group: 'error',
            title: 'Error',
            text: 'Failed to delete card. Please try again.',
        });
    } finally {
        showDeleteConfirmation.value = false;
    }
};

const handleAddProductChange = async (card) => {
    // This will trigger the edit card modal with product change
    emit('open-edit-card', card.id);
};

const handleEditProductChange = async (card, productChange) => {
    // This will trigger the edit card modal with specific product change
    emit('open-edit-card', card.id, { editingProductChange: productChange });
};

const handleDeleteProductChange = async (card, productChangeId) => {
    try {
        await axios.delete(
            `/api/wallets/cards/${card.id}/product-changes/${productChangeId}`
        );

        // Refresh wallet data
        await walletStore.getWallet();

        // Update selectedCard with fresh data to trigger Vue reactivity
        if (selectedCard.value) {
            const updatedCard = walletCards.value.find(
                (c) => c.id === selectedCard.value.id
            );
            if (updatedCard) {
                setSelectedCard(updatedCard);
            } else {
                // Card no longer exists in wallet
                emit('close');
            }
        }

        // Show success message
        notify({
            group: 'success',
            text: 'Product change deleted successfully',
        });
    } catch (error) {
        console.error('Error deleting product change:', error);
        notify({
            group: 'error',
            text: 'Failed to delete product change',
        });
    }
};
</script>

<style scoped>
/* Fade transition for delete confirmation */
.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}

.fade-enter-to,
.fade-leave-from {
    opacity: 1;
}
</style>
